import React from 'react';

interface AcceptedBid {
  totalProjectAmount?: number;
  amount?: number;
}

interface EscrowAccount {
  totalDeposited?: number;
}

interface ActiveProject {
  id: string;
  address: string;
  projectType?: string;
  status: string;
  acceptedBid?: AcceptedBid;
  escrowAccount?: EscrowAccount;
}

interface ActiveProjectCardProps {
  project: ActiveProject;
  onNavigate: (screen: string) => void;
}

/**
 * Displays a single active project card in the contractor dashboard
 * Shows project details, status, and bid amount
 */
export default function ActiveProjectCard({
  project,
  onNavigate,
}: ActiveProjectCardProps) {
  // Compute the project amount the homeowner is paying (bid + platform fee).
  // Prefer totalProjectAmount from escrow; if it's missing or clearly equal to the raw bid,
  // fall back to bid + 10% platform fee.
  const rawTotalCents = project.acceptedBid?.totalProjectAmount ?? project.acceptedBid?.amount ?? 0;
  const rawBidCents = project.acceptedBid?.amount ?? 0;
  let bidAmountCents = rawTotalCents;
  if (rawBidCents > 0 && (rawTotalCents === 0 || rawTotalCents < rawBidCents * 1.05)) {
    // totalProjectAmount not set or looks like just the bid → approximate 10% fee
    bidAmountCents = Math.round(rawBidCents * 1.1);
  }
  const bidAmount = bidAmountCents / 100;

  const STATUS_LABELS: Record<string, string> = {
    ACTIVE_P1: 'Active - Phase 1',
    ACTIVE_P2: 'Active - Phase 2',
    ACTIVE_P3: 'Active - Phase 3',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    SCHEDULED: 'Scheduled',
  };
  const displayStatus: string = STATUS_LABELS[project.status] ?? project.status ?? 'ACTIVE';

  const isCompleted = project.status === 'COMPLETED';

  return (
    <div
      key={project.id}
      style={{
        border: `1px solid ${isCompleted ? '#6b7280' : '#10b981'}`,
        backgroundColor: isCompleted ? '#f9fafb' : '#f0fdf4',
        borderRadius: '8px',
        padding: '12px',
        cursor: 'pointer'
      }}
      onClick={() => onNavigate(`/contractor/projects/${project.id}`)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>
            {(project.projectType || 'Roof')} Replacement
          </h4>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>{project.address}</p>
        </div>
        <span className="status-badge status-success" style={{ fontSize: '11px', padding: '2px 6px' }}>
          Accepted
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ color: '#6b7280', fontSize: '12px' }}>
          {displayStatus}
        </span>
        <span style={{ color: '#10b981', fontWeight: 600, fontSize: '14px' }}>
          ${bidAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
}
